import Menu from "components/Menu";
import UserProfile from "components/UserProfile";
import { routeConstants } from "constants/routeConstants";
import { LogoWrapper } from "pages/shared";
import { FC, memo, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "slice";
import { updateNewJobData } from "slice/jobs";
import appTheme from "theme";
import { WithStyle } from "utils/WithStyle";
import { ReactComponent as AlimitlessWhiteLogo } from "../../icons/alimitless-logo-white.svg";
import { ReactComponent as AlimitLess } from "../../images/limitlessLogo.svg";
import { ReactComponent as Logo } from "../../images/logo.svg";
import * as Styled from "./Navbar.styled";
import { NavbarProps } from "./types";

const Component: FC<NavbarProps> = memo(
    ({
        hidden = false,
        backgroundColor = appTheme.colors.white,
        showBoxShadow = true,
    }) => {
        const { pathname } = useLocation();
        const dispatch = useAppDispatch();
        const { id: userId, agency: is_agency } = useAppSelector(
            (state) => state.user
        );
        const [menuOpen, setMenuOpen] = useState(false);
        const navigateTo = useNavigate();
        const showAuthButton = useMemo(
            () =>
                [
                    "/login",
                    "/sign-up",
                    "/forgot-password",
                    "/reset-password",
                ].includes(pathname),
            [pathname]
        );

        const authButtonProps = useMemo(() => {
            if (pathname === "/login") {
                return {
                    url: "/sign-up",
                    label: "Sign Up",
                };
            } else if (
                pathname === "/sign-up" ||
                pathname === "/forgot-password" ||
                pathname === "/reset-password"
            ) {
                return {
                    url: "/login",
                    label: "Login",
                };
            }
        }, [pathname]);

        const logo = useMemo(() => {
            if (menuOpen) {
                return <AlimitlessWhiteLogo />;
            } else {
                return (
                    <>
                        <Logo />
                        <AlimitLess className="alimitless-logo" />
                    </>
                );
            }
        }, [menuOpen]);

        useEffect(() => {
            if (window && menuOpen) {
                window.document.body.style.overflow = "hidden";
            } else {
                window.document.body.style.overflow = "unset";
            }
        }, [menuOpen]);

        return (
            <Styled.NavbarWrapper
                hidden={hidden}
                backgroundColor={backgroundColor}
                showBoxShadow={showBoxShadow}
            >
                <Styled.InnerWrapper>
                    <LogoWrapper
                        style={{ marginLeft: "45px" }}
                        // href={
                        //     process.env.REACT_APP_WEBSITE_URL?.toString() || "/"
                        // }
                        onClick={() => {
                            if (userId) {
                                navigateTo("/");
                            } else {
                                window.location.href =
                                    process.env.REACT_APP_WEBSITE_URL?.toString() ||
                                    "/";
                            }
                        }}
                    >
                        {logo}
                    </LogoWrapper>

                    <Styled.Nav style={{ marginRight: "45px" }}>
                        <Styled.NavList>
                            {showAuthButton && authButtonProps && (
                                <>
                                    <Styled.NavItem>
                                        <Styled.AuthButton
                                            to={authButtonProps.url}
                                        >
                                            {authButtonProps.label}
                                        </Styled.AuthButton>
                                    </Styled.NavItem>
                                </>
                            )}

                            {userId ? (
                                <>
                                    {!is_agency && (
                                        <Styled.NavItem>
                                            <Styled.NavLink
                                                to="/find-work"
                                                isActive={
                                                    pathname === "/find-work"
                                                }
                                            >
                                                Find Casting
                                            </Styled.NavLink>
                                        </Styled.NavItem>
                                    )}
                                    {is_agency && (
                                        <>
                                            <Styled.NavItem>
                                                <Styled.NavLink
                                                    to="/find-talent"
                                                    isActive={
                                                        pathname ===
                                                        "/find-talent"
                                                    }
                                                >
                                                    Find Talent
                                                </Styled.NavLink>
                                            </Styled.NavItem>
                                            <Styled.NavItem>
                                                <Styled.NavLink
                                                    to={
                                                        routeConstants.jobs
                                                            .postAJob.root +
                                                        routeConstants.jobs
                                                            .postAJob.category
                                                    }
                                                    isActive={pathname.includes(
                                                        routeConstants.jobs
                                                            .postAJob.category
                                                    )}
                                                    onClick={() => {
                                                        dispatch(
                                                            updateNewJobData({})
                                                        );
                                                    }}
                                                >
                                                    Post Casting
                                                </Styled.NavLink>
                                            </Styled.NavItem>
                                        </>
                                    )}
                                    <Menu
                                        menuOpen={menuOpen}
                                        setMenuOpen={setMenuOpen}
                                        is_agency={!!is_agency}
                                    />
                                    <UserProfile />
                                </>
                            ) : null}
                        </Styled.NavList>
                    </Styled.Nav>
                </Styled.InnerWrapper>
            </Styled.NavbarWrapper>
        );
    }
);

export const Navbar: FC<NavbarProps> & WithStyle = Object.assign(Component, {
    Style: Styled.NavbarWrapper,
});
